import { useCallback, useEffect, useRef, useState } from "react";
import { loadBlob } from "./blob-store";
import {
  appendSnapshot,
  loadSnapshotMeta,
  removeSnapshot,
  type SnapshotKind,
  type SnapshotMeta,
} from "./snapshots";

/**
 * Shared snapshot hook driving the WS4 facilitation tools (HAH + STR).
 * Each consumer wires in two callbacks:
 *
 *   - `captureBlob` produces a PNG `Blob` of the tool's current
 *     visual state. HAH uses the canvas (`toBlob`); STR composes a
 *     DOM-to-PNG render of its board.
 *   - `restoreFromSnapshot` (passed in via options) applies a meta +
 *     its blob back to the live tool. The blob fetch is done inside
 *     the hook so consumers only see the meta + raw blob.
 *
 * The hook returns the meta list (newest first), plus `saveManual` /
 * `saveAuto` / `restore` / `remove` callbacks. The manual save is
 * fire-and-forget; the caller decides whether to chain it into an
 * AssetLocker `add()` (HAH and STR both do, with tool-specific
 * labelling).
 *
 * StrictMode-safe: all async writes guard against unmount via a
 * `disposed` ref, and the hook never schedules `setState` from a
 * teardown'd render pass.
 */

export type RestoreFn = (meta: SnapshotMeta, blob: Blob) => void | Promise<void>;

export type UseSnapshotOptions = {
  workshopId: string;
  kind: SnapshotKind;
  captureBlob: () => Promise<Blob | null>;
  restoreFromSnapshot: RestoreFn;
  /**
   * Optional structured state captured alongside the PNG. Tools that
   * can't reverse a render (StressTest) supply a JSON serialiser so
   * `restore()` has enough to rehydrate. Returning `undefined`
   * means "no structured state needed" (HAH path).
   */
  captureStateJson?: () => string | undefined;
};

export type UseSnapshotResult = {
  snapshots: SnapshotMeta[];
  /** True once the initial meta-list load resolves. */
  hydrated: boolean;
  /**
   * Capture + persist a manual snapshot. Returns the new meta so
   * callers can chain it into an AssetLocker `add()` (carrying the
   * `blobKey` + thumbnail forward). Returns `null` if the capture
   * step failed (e.g. canvas not mounted yet).
   */
  saveManual: () => Promise<SnapshotMeta | null>;
  /**
   * Capture + persist an auto snapshot. Subject to the FIFO cap in
   * `snapshots.ts`. Silently no-ops on capture failure.
   */
  saveAuto: () => Promise<void>;
  restore: (id: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
};

export function useSnapshot(opts: UseSnapshotOptions): UseSnapshotResult {
  const { workshopId, kind, captureBlob, restoreFromSnapshot, captureStateJson } =
    opts;

  const [snapshots, setSnapshots] = useState<SnapshotMeta[]>([]);
  const [hydrated, setHydrated] = useState(false);
  // Guards `setState` after teardown — see `useHandshakeStrokes` for
  // the same StrictMode pattern.
  const disposedRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;
    let cancelled = false;
    setHydrated(false);
    void loadSnapshotMeta(kind, workshopId)
      .then((list) => {
        if (cancelled || disposedRef.current) return;
        setSnapshots(list);
        setHydrated(true);
      })
      .catch(() => {
        if (cancelled || disposedRef.current) return;
        setSnapshots([]);
        setHydrated(true);
      });
    return () => {
      cancelled = true;
      disposedRef.current = true;
    };
  }, [kind, workshopId]);

  const saveManual = useCallback(async (): Promise<SnapshotMeta | null> => {
    const blob = await captureBlob();
    if (!blob) return null;
    const stateJson = captureStateJson?.();
    const { meta, list } = await appendSnapshot({
      kind,
      workshopId,
      blob,
      source: "manual",
      stateJson,
    });
    if (!disposedRef.current) {
      setSnapshots(list);
    }
    return meta;
  }, [captureBlob, captureStateJson, kind, workshopId]);

  const saveAuto = useCallback(async (): Promise<void> => {
    const blob = await captureBlob();
    if (!blob) return;
    const stateJson = captureStateJson?.();
    const { list } = await appendSnapshot({
      kind,
      workshopId,
      blob,
      source: "auto",
      stateJson,
    });
    if (!disposedRef.current) {
      setSnapshots(list);
    }
  }, [captureBlob, captureStateJson, kind, workshopId]);

  const restore = useCallback(
    async (id: string): Promise<void> => {
      const meta = snapshots.find((m) => m.id === id);
      if (!meta) return;
      const blob = await loadBlob(meta.blobKey);
      if (!blob) return;
      await restoreFromSnapshot(meta, blob);
    },
    [snapshots, restoreFromSnapshot],
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      const next = await removeSnapshot(kind, workshopId, id);
      if (!disposedRef.current) {
        setSnapshots(next);
      }
    },
    [kind, workshopId],
  );

  return { snapshots, hydrated, saveManual, saveAuto, restore, remove };
}
